export type ProviderSelectionMode =
  | "SINGLE_ACTIVE"
  | "MULTI_ACTIVE"
  | "PRIMARY_WITH_FALLBACKS"
  | "LOAD_BALANCED"
  | "ROUND_ROBIN"
  | "RANKED"
  | "MANUAL"
  | "AUTO_BEST";

export interface ProviderCandidate {
  readonly instanceId: string;
  readonly provider: string;
  readonly enabled: boolean;
  readonly primary?: boolean;
  readonly fallbackRank?: number;
  readonly health: "HEALTHY" | "DEGRADED" | "OFFLINE" | "UNKNOWN";
  readonly score?: number;
}

export interface ProviderSelectionGroup {
  readonly groupId: string;
  readonly projectId: string;
  readonly capability: string;
  readonly mode: ProviderSelectionMode;
  readonly allowZeroActive: boolean;
  readonly minimumActive: number;
  readonly maximumActive?: number;
  readonly candidates: readonly ProviderCandidate[];
  readonly updatedAt: string;
}

export interface ProviderSelectionEvent {
  readonly groupId: string;
  readonly projectId: string;
  readonly actorId: string;
  readonly instanceId: string;
  readonly enabled: boolean;
  readonly mode: ProviderSelectionMode;
  readonly previousEnabledInstanceIds: readonly string[];
  readonly nextEnabledInstanceIds: readonly string[];
  readonly createdAt: string;
}

function validate(group: ProviderSelectionGroup): void {
  const active = group.candidates.filter((candidate) => candidate.enabled);

  if (!group.allowZeroActive && active.length === 0) {
    throw new Error("Provider group requires at least one active instance.");
  }

  if (active.length < group.minimumActive) {
    throw new Error("Provider group is below its minimum active count.");
  }

  if (
    group.maximumActive !== undefined &&
    active.length > group.maximumActive
  ) {
    throw new Error("Provider group exceeds its maximum active count.");
  }

  if (group.mode === "SINGLE_ACTIVE" && active.length > 1) {
    throw new Error("SINGLE_ACTIVE permits at most one active instance.");
  }

  if (group.mode === "PRIMARY_WITH_FALLBACKS") {
    const primaries = active.filter((candidate) => candidate.primary);

    if (active.length > 0 && primaries.length !== 1) {
      throw new Error(
        "PRIMARY_WITH_FALLBACKS requires exactly one enabled primary.",
      );
    }
  }
}

export function toggleProviderInstance(input: {
  readonly group: ProviderSelectionGroup;
  readonly instanceId: string;
  readonly enabled: boolean;
  readonly actorId: string;
  readonly now?: string;
}): { group: ProviderSelectionGroup; event: ProviderSelectionEvent } {
  const target = input.group.candidates.find(
    (candidate) => candidate.instanceId === input.instanceId,
  );

  if (!target) {
    throw new Error(`Unknown provider instance: ${input.instanceId}`);
  }

  const previousEnabledInstanceIds = input.group.candidates
    .filter((candidate) => candidate.enabled)
    .map((candidate) => candidate.instanceId);

  let candidates = input.group.candidates.map((candidate) => ({ ...candidate }));

  if (input.group.mode === "SINGLE_ACTIVE" && input.enabled) {
    candidates = candidates.map((candidate) => ({
      ...candidate,
      enabled: candidate.instanceId === input.instanceId,
      primary: candidate.instanceId === input.instanceId,
    }));
  } else if (
    input.group.mode === "PRIMARY_WITH_FALLBACKS" &&
    input.enabled
  ) {
    const alreadyHasPrimary = candidates.some(
      (candidate) => candidate.enabled && candidate.primary,
    );

    candidates = candidates.map((candidate) =>
      candidate.instanceId === input.instanceId
        ? {
            ...candidate,
            enabled: true,
            primary: candidate.primary || !alreadyHasPrimary,
          }
        : candidate,
    );
  } else {
    candidates = candidates.map((candidate) =>
      candidate.instanceId === input.instanceId
        ? { ...candidate, enabled: input.enabled, primary: input.enabled && candidate.primary }
        : candidate,
    );
  }

  const now = input.now ?? new Date().toISOString();
  const group: ProviderSelectionGroup = {
    ...input.group,
    candidates,
    updatedAt: now,
  };

  validate(group);

  const nextEnabledInstanceIds = group.candidates
    .filter((candidate) => candidate.enabled)
    .map((candidate) => candidate.instanceId);

  return {
    group,
    event: {
      groupId: group.groupId,
      projectId: group.projectId,
      actorId: input.actorId,
      instanceId: input.instanceId,
      enabled: input.enabled,
      mode: group.mode,
      previousEnabledInstanceIds,
      nextEnabledInstanceIds,
      createdAt: now,
    },
  };
}

export function selectProvider(
  group: ProviderSelectionGroup,
): ProviderCandidate | null {
  validate(group);

  const available = group.candidates.filter(
    (candidate) => candidate.enabled && candidate.health !== "OFFLINE",
  );

  if (available.length === 0) return null;

  if (group.mode === "PRIMARY_WITH_FALLBACKS") {
    return (
      available.find((candidate) => candidate.primary) ??
      [...available].sort(
        (left, right) =>
          (left.fallbackRank ?? Number.MAX_SAFE_INTEGER) -
          (right.fallbackRank ?? Number.MAX_SAFE_INTEGER),
      )[0] ??
      null
    );
  }

  if (group.mode === "AUTO_BEST" || group.mode === "RANKED") {
    return [...available].sort(
      (left, right) => (right.score ?? 0) - (left.score ?? 0),
    )[0] ?? null;
  }

  return available[0] ?? null;
}
